import React, { CSSProperties, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactSelect, {
  components,
  GetOptionLabel,
  GetOptionValue,
  GroupBase,
  Props,
  SelectComponentsConfig,
} from 'react-select'
import Select from 'react-select/dist/declarations/src/Select'
import styled from 'styled-components'
import { Progress } from '../../../globalTypes'
import { FormField } from '../../form/FormField/FormField'
import { IconCrossCircle } from '../../iconography'
import { Option, OptionWithAnimatedText, OptionWithWrappedText } from './components/Option'
import {
  baseCustomStyles,
  ClearIndicatorStyled,
  CustomSelectStyles,
  DropdownIndicatorStyled,
  smallSelectStyles,
} from './styles'

export type BasicSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<
  Props<Option, IsMulti, Group>,
  'loadingMessage' | 'styles' | 'onInputChange' | 'inputValue' | 'getOptionValue' | 'getOptionLabel'
> & {
  /**
   * If Progress.WORK is passed the select will be showing loading animation.
   */
  progress?: Progress
  /**
   * Message to show when select is loading
   */
  loadingMessage?: string
  label?: string
  invalid?: boolean
  errorText?: string
  /**
   *
   * @deprecated use `components` or `variant` instead
   */
  customStyles?: CustomSelectStyles
  labelStyle?: CSSProperties
  required?: boolean
  openMenuOnClick?: boolean
  openMenuOnFocus?: boolean
  disableFilterOnSearchChange?: boolean
  /**
   * Fired when the select's input value changes
   */
  onSearchChange?: (searchValue: string) => void
  /**
   * Value of search input
   */
  searchValue?: string
  id?: string
  optional?: boolean
  /**
   *
   * Small variant makes the select's control smaller
   */
  variant?: 'small' | 'default'
  /**
   * Width of the select's control
   * @default '100%'
   */
  width?: React.CSSProperties['width']
  /**
   * Min width of the select's control
   * @default '0'
   */
  minWidth?: React.CSSProperties['minWidth']
  /**
   * Max width of the select's control
   * @default 'none'
   */
  maxWidth?: React.CSSProperties['maxWidth']
  /**
   * Width of the select's menu
   * @default 'auto'
   */
  menuWidth?: React.CSSProperties['width']
  /**
   * Min width of the select's menu
   * @default '100%'
   */
  menuMinWidth?: React.CSSProperties['minWidth']
  /**
   * Max width of the select's menu
   * @default '175%'
   */
  menuMaxWidth?: React.CSSProperties['maxWidth']
  /**
   * Max height of the select's menu
   * @default '50vh'
   */
  menuMaxHeight?: React.CSSProperties['maxHeight']
  /**
   * Function to get the value of an option
   */
  getOptionValue?: GetOptionValue<Option>
  /**
   * Function to get the label of an option
   */
  getOptionLabel?: GetOptionLabel<Option>
  /**
   * If `true`, then the search input will not be cleared when an option is selected.
   *
   * @default same as isMulti
   */
  disableSearchClearOnSelect?: boolean
  /**
   * If `true`, text that overflows the option width will be animated to show all content.
   * Doesn't work together with `wrapOptionsText`
   */
  animateOverflowingText?: boolean
  /**
   * If `true`, text will be wrapped to new lines on word boundaries. Doesn't work together with `animateOverflowingText`.
   */
  wrapOptionsText?: boolean
  /**
   * If `true`, text in the control will be wrapped to new lines on word boundaries.
   */
  wrapValueText?: boolean
  /**
   * Disables filtering options by search string.
   * */
  disableFilterOptionsOnSearchChange?: boolean
  'data-test-id'?: string
  /**
   * Show value as text without control.
   */
  showValueAsPlainText?: boolean
  /**
   * If `true`, tags will not be shown in the control for multi-select.
   */
  disableTags?: boolean
}

export const StyledMultiValueContainer = styled.div`
  display: grid;
  grid-template-columns: auto max-content;
  align-items: center;
  background: rgba(3, 123, 255, 0.08);
  border-radius: 4px;
  box-sizing: border-box;
  max-width: 80%;
`

export const StyledMultiValueLabel = styled.div`
  padding: 4px 0 4px 8px;
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const StyledMultiValueRemove = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 4px 6px;
  cursor: pointer;

  & > svg {
    transition: 0.2s opacity ease;
  }

  &:hover,
  &:focus {
    & > svg {
      opacity: 0.8;
    }
  }
`

export const StyledPlaceholder = styled(components.Placeholder)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const StyledMenu = styled(components.Menu)<{
  width: React.CSSProperties['maxWidth']
  maxWidth: React.CSSProperties['maxWidth']
  minWidth: React.CSSProperties['minWidth']
}>`
  z-index: 10 !important;

  width: ${({ width }) => width} !important;
  min-width: ${({ minWidth }) => minWidth} !important;
  max-width: ${({ maxWidth }) => maxWidth} !important;
`

export const StyledMenuList = styled(components.MenuList)<{
  maxHeight: React.CSSProperties['maxHeight']
}>`
  padding: 0 4px 4px 4px !important;
  max-height: ${({ maxHeight }) => maxHeight} !important;
`

export const SingleValueStyled = styled(components.SingleValue)`
  overflow: visible !important;
  word-break: break-word !important;
  text-overflow: unset !important;
  white-space: normal !important;
`

const DropdownIndicator: any = ({ selectProps }) => <DropdownIndicatorStyled menuIsOpen={!!selectProps.menuIsOpen} />
const ClearIndicator: any = ({ innerProps }) => (
  <div {...innerProps}>
    <ClearIndicatorStyled />
  </div>
)
const MultiValueContainer: any = ({ children }) => <StyledMultiValueContainer>{children}</StyledMultiValueContainer>
const MultiValueLabel: any = ({ children }) => <StyledMultiValueLabel>{children}</StyledMultiValueLabel>
const MultiValueRemove: any = ({ innerProps: { ref: _, className: __, ...innerProps } }) => (
  <StyledMultiValueRemove {...innerProps}>
    <IconCrossCircle />
  </StyledMultiValueRemove>
)

const Placeholder: any = props => <StyledPlaceholder {...props} />

const SingleValueWithWrappedText: any = props => <SingleValueStyled {...props} />

const DISABLED_FILTER_CB = () => true

/**
 * Basic select without any async logic and value is an object.
 */
const BasicSelectForward = React.forwardRef(
  <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    {
      id,
      value,
      label,
      invalid = false,
      errorText,
      customStyles = {},
      labelStyle = {},
      menuPlacement,
      required,
      className,
      name,
      components: componentsFromProps,
      progress,
      loadingMessage = 'Loading ...',
      optional,
      onSearchChange,
      variant = 'default',
      disableFilterOnSearchChange,
      searchValue,
      minWidth = '0',
      maxWidth = 'none',
      width = '100%',
      menuMinWidth = '100%',
      menuMaxWidth = '175%',
      menuWidth = 'auto',
      menuMaxHeight = '50vh',
      disableTags,
      isMulti,
      animateOverflowingText,
      disableSearchClearOnSelect = isMulti,
      disableFilterOptionsOnSearchChange,
      wrapOptionsText,
      wrapValueText,
      options,
      filterOption: filterOptionFromProps,
      hideSelectedOptions = isMulti && !disableTags,
      'data-test-id': dataTestId = 'select',
      closeMenuOnSelect = !isMulti,
      menuIsOpen,
      onMenuOpen,
      onMenuClose,
      ...props
    }: BasicSelectProps<Option, IsMulti, Group>,
    ref: any,
  ) => {
    const { t } = useTranslation('ui')
    const [menuOpen, setMenuOpen] = useState(false)
    const [inputValue, setInputValue] = useState(searchValue)
    const smallStyles = variant === 'small' ? smallSelectStyles(invalid) : {}

    useEffect(() => {
      if (searchValue !== inputValue) setInputValue(searchValue)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue])

    const MultiValue: SelectComponentsConfig<Option, IsMulti, Group>['MultiValue'] = useCallback(
      props => (disableTags ? null : <components.MultiValue {...props} />),
      [disableTags],
    )

    const ValueContainer: SelectComponentsConfig<Option, IsMulti, Group>['ValueContainer'] = useCallback(
      ({ selectProps, children, ...props }) => {
        return (
          <components.ValueContainer selectProps={selectProps} data-test-id="value-container" {...props}>
            {!!(disableTags && Array.isArray(selectProps.value) && selectProps.value.length) &&
              (selectProps.value.length === 1
                ? t('BasicSelect.one-item-selected')
                : t('BasicSelect.nth-items-selected', { count: selectProps.value.length }))}
            {children}
          </components.ValueContainer>
        )
      },
      [disableTags, t],
    )

    const Menu: SelectComponentsConfig<Option, IsMulti, Group>['Menu'] = useCallback(
      props => <StyledMenu {...props} minWidth={menuMinWidth} maxWidth={menuMaxWidth} width={menuWidth} />,
      [menuMaxWidth, menuMinWidth, menuWidth],
    )

    const MenuList: SelectComponentsConfig<Option, IsMulti, Group>['MenuList'] = useCallback(
      ({ children, ...props }) => (
        <StyledMenuList {...props} maxHeight={menuMaxHeight}>
          {children}
        </StyledMenuList>
      ),
      [menuMaxHeight],
    )

    const handleMenuOpen = useCallback(() => {
      setMenuOpen(true)
      onMenuOpen?.()
    }, [onMenuOpen])

    const handleMenuClose = useCallback(() => {
      setMenuOpen(false)
      onMenuClose?.()
    }, [onMenuClose])

    return (
      <FormField
        className={className}
        label={label}
        labelStyle={labelStyle}
        invalid={invalid}
        errorText={errorText}
        required={required}
        name={name}
        id={id}
        optional={optional}
        width={width}
        maxWidth={maxWidth}
        minWidth={minWidth}
        testId={dataTestId}
        data-is-multi={isMulti}
        data-is-open={menuIsOpen || menuOpen}
        render={({ id, name }) => (
          <ReactSelect
            isMulti={isMulti}
            inputId={id}
            name={name}
            isLoading={progress === Progress.WORK}
            loadingMessage={() => loadingMessage}
            value={value || null}
            options={options}
            styles={{
              ...baseCustomStyles(invalid),
              ...smallStyles,
              ...customStyles,
            }}
            ref={ref}
            data-test-id="select"
            inputValue={inputValue}
            filterOption={
              filterOptionFromProps
                ? filterOptionFromProps
                : disableFilterOptionsOnSearchChange
                ? DISABLED_FILTER_CB
                : undefined
            }
            onInputChange={(value, actionMeta) => {
              if (
                !disableSearchClearOnSelect ||
                actionMeta.action === 'input-change' ||
                actionMeta.action === 'menu-close'
              ) {
                setInputValue(value)
                if (!disableFilterOnSearchChange || actionMeta.action === 'input-change') {
                  onSearchChange?.(value)
                }
              }
            }}
            menuIsOpen={menuIsOpen || menuOpen}
            onMenuClose={handleMenuClose}
            onMenuOpen={handleMenuOpen}
            closeMenuOnSelect={closeMenuOnSelect}
            components={{
              Placeholder,
              ValueContainer,
              DropdownIndicator,
              ClearIndicator,
              MultiValue,
              MultiValueContainer,
              MultiValueLabel,
              MultiValueRemove,
              Option:
                animateOverflowingText && !wrapOptionsText
                  ? OptionWithAnimatedText
                  : wrapOptionsText
                  ? OptionWithWrappedText
                  : Option,
              Menu,
              MenuList,
              SingleValue: wrapValueText ? SingleValueWithWrappedText : components.SingleValue,
              ...componentsFromProps,
            }}
            menuPlacement={menuPlacement}
            hideSelectedOptions={hideSelectedOptions}
            {...props}
          />
        )}
      />
    )
  },
)

export const BasicSelect = BasicSelectForward as <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: BasicSelectProps<Option, IsMulti, Group> & { ref?: React.RefObject<Select<Option, IsMulti, Group>> },
) => JSX.Element
